import React, { Fragment, useEffect, useState } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import FromPageIndicator from "../Common/FromPageIndicator";

const OrganizingCommittee = () => {
  const [committeeData, setCommitteeData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch data from API when component mounts
    fetchCommitteeData();
  }, []);

  const fetchCommitteeData = async () => {
    setLoading(true);
    try {
      // API call to fetch committee data
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8921_TnpyAmpicon_Create_Staff_List"
      );

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const data = await response.json();

      // For any member without categoryName, set it based on Role
      const processedData = data.map((member) => {
        if (!member.categoryName || member.categoryName === "undefined") {
          member.categoryName = member.Role.includes("Chief Patron")
            ? "Chief Patrons"
            : member.Role.includes("Patron")
            ? "Patrons"
            : member.Role;
        }

        // Format the full designation with hospital and location
        let fullDesignation = member.designation || "";
        if (member.hospital)
          fullDesignation += member.hospital ? ` ${member.hospital}` : "";
        if (member.location)
          fullDesignation += member.location ? ` ${member.location}` : "";

        return {
          ...member,
          fullDesignation,
        };
      });

      // Sort members by position within each category
      processedData.sort((a, b) => {
        if (a.categoryId === b.categoryId) {
          return parseInt(a.Position) - parseInt(b.Position);
        }
        return parseInt(a.categoryId) - parseInt(b.categoryId);
      });

      setCommitteeData(processedData);

      // Group data by categories
      const categoryMap = {};
      processedData.forEach((member) => {
        if (!categoryMap[member.categoryId]) {
          categoryMap[member.categoryId] = {
            id: member.categoryId,
            name: member.categoryName,
            members: [],
          };
        }
        categoryMap[member.categoryId].members.push(member);
      });

      // Convert to array and sort by categoryId
      const categoriesArray = Object.values(categoryMap);
      categoriesArray.sort((a, b) => parseInt(a.id) - parseInt(b.id));
      setCategories(categoriesArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching committee data:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Special rendering for Chief Patrons section
  const renderChiefPatrons = (chiefPatrons) => {
    if (!chiefPatrons || chiefPatrons.length === 0) return null;

    return (
      <section className="py-4" style={{ backgroundColor: "#FFF4FA" }}>
        <Container>
          <h3 className="mb-4 fw-bold text-center">Chief Patrons</h3>

          {/* First row with only the first chief patron, with bigger size */}
          <Row className="justify-content-center">
            <Col
              xs={12}
              sm={6}
              md={4}
              lg={4}
              className="d-flex flex-column align-items-center mb-4"
            >
              <Image
                src={`https://api.dremerz.net/uploads/${chiefPatrons[0].image}`}
                roundedCircle
                alt={chiefPatrons[0].name}
                width={180}
                height={180}
                className="border border-4 border-danger-subtle shadow-sm"
                onError={(e) => {
                  e.target.src = "/assets/avatar.jpg";
                }}
              />
              <p className="mt-3 mb-1 fw-semibold fs-5 text-center">
                {chiefPatrons[0].Role}
              </p>
              <p className="text-danger fw-bold mb-1 fs-4 text-center">
                {chiefPatrons[0].name}
              </p>
              <p className="mb-0 text-muted fs-6 text-center">
                {chiefPatrons[0].fullDesignation}
              </p>
            </Col>
          </Row>

          {/* Second row with the remaining chief patrons */}
          <Row className="justify-content-center">
            {chiefPatrons.slice(1).map((member) => (
              <Col
                key={member.id}
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className="d-flex flex-column align-items-center mb-4"
              >
                <Image
                  src={`https://api.dremerz.net/uploads/${member.image}`}
                  roundedCircle
                  alt={member.name}
                  width={150}
                  height={150}
                  className="border border-4 border-danger-subtle shadow-sm object-fit-cover"
                  onError={(e) => {
                    e.target.src = "/assets/avatar.jpg";
                  }}
                />
                <p className="mt-3 mb-1 fw-semibold text-center">
                  {member.Role}
                </p>
                <p className="text-danger fw-bold mb-1 text-center">
                  {member.name}
                </p>
                <p className="mb-0 text-muted small text-center">
                  {member.fullDesignation}
                </p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    );
  };

  // Render regular committee sections
  const renderCommitteeSection = (category) => {
    return (
      <section
        key={category.id}
        className="py-4"
        style={{ backgroundColor: "#FFF4FA" }}
      >
        <Container>
          <h3 className="mb-4 fw-bold text-center">{category.name}</h3>
          <Row className="justify-content-center">
            {category.members.map((member) => (
              <Col
                key={member.id}
                xs={12}
                sm={6}
                md={4}
                lg={3}
                className="d-flex flex-column align-items-center mb-4"
              >
                <Image
                  src={`https://api.dremerz.net/uploads/${member.image}`}
                  roundedCircle
                  alt={member.name}
                  width={150}
                  height={150}
                  className="border border-4 border-danger-subtle shadow-sm"
                  onError={(e) => {
                    e.target.src = "/assets/avatar.jpg";
                  }}
                />
                <p className="mt-3 mb-1 fw-semibold text-center">
                  {member.Role}
                </p>
                <p className="text-danger fw-bold mb-1 text-center">
                  {member.name}
                </p>
                <p className="mb-0 text-muted small text-center">
                  {member.fullDesignation}
                </p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="py-5 text-center">
        <Container>
          <h3>Loading committee members...</h3>
        </Container>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="py-5 text-center">
        <Container>
          <h3 className="text-danger">Error loading committee data</h3>
          <p>{error}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => fetchCommitteeData()}
          >
            Try Again
          </button>
        </Container>
      </div>
    );
  }

  return (
    <Fragment>
      <FromPageIndicator />

      {/* Render all categories starting with Chief Patrons */}
      {categories.map((category, index) => {
        if (category.name === "Chief Patrons") {
          return renderChiefPatrons(category.members);
        } else {
          return renderCommitteeSection(category);
        }
      })}

      {/* Placeholder for any additional sections */}
      {categories.length === 0 && (
        <section className="py-5" style={{ backgroundColor: "#FFF4FA" }}>
          <Container>
            <p className="text-center">No committee members found.</p>
          </Container>
        </section>
      )}
    </Fragment>
  );
};

export default OrganizingCommittee;
